/* 文章详情页 */
import React, { useState, useEffect, useRef, createContext } from 'react'
import get from 'lodash/get'
import fetch from 'lib/decorated-fetch'

import getUrl from 'lib/url'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import fetchFeed, { IFeedData } from 'lib/fetch-feed'
import redirectTo from 'lib/redirect-to'

import Head from 'Components/head'
import Statement from 'Components/statement'
import Breadcrumb from 'Components/breadcrumb'
import Author, { IAuthor } from 'Components/author'
import CarSystem, { ICarSystem } from 'Components/car-system'
import QRCode from 'Components/qr-code'
import {
  DEFAULT_VIDEO_KEYWORDS,
  DEFAULT_VIDEO_DESCRIPTION,
  DEFAULT_ARTICLE_KEYWORDS,
  DEFAULT_ARTICLE_DESCRIPTION,
} from 'Constants/index'

import Content from './content'
import RaletedVideo from './related-video'
import Recommend, { IRecommend } from './recommend'
import Comment from 'Components/comment'

import HotSpot from '../home/old-components/hot-spot'
import HotLeaderBoard from '../home/old-components/popular-leaderboard'
import useProcessSpy from 'Components/hooks/use-process-spy'
import useHandleBeforeLeave from 'Components/hooks/use-handle-before-leave'
import { getBoundingClientRect } from 'lib/utils'

interface IArticleContent {
  [props: number]: {
    value: string
    label: string
    schemaPath: string
  }
}
const ARTICLE_CONTENT_TYPE: IArticleContent = {
  1: {
    value: 'pgc_article',
    label: '文章',
    schemaPath: 'detail',
  },
  2: {
    value: 'pgc_video',
    label: '视频',
    schemaPath: 'detail',
  },
  3: {
    value: 'ugc_video',
    label: '小视频',
    schemaPath: 'ugc_detail',
  },
  4: {
    value: 'pgc_gallery',
    label: '图集',
    schemaPath: 'detail',
  },
  5: {
    value: 'ugc_question',
    label: '问答',
    schemaPath: 'question_detail',
  },
  6: {
    value: 'ugc_article',
    label: '微头条',
    schemaPath: 'drivers_detail',
  },
}

export interface IArticle {
  content: string
  publish_time: number
  source: string
  title: string
  car_card: {
    agent_price: string
    cover: string
    offical_price: string
    series_id: string
    series_name: string
    web_url: string
  }
  media_user: {
    avatar_url: string
    screen_name: string
  }
  video_info: {
    vid: string
  }
  article_type: number
}

interface IProps {
  gid: string
  /**
   * 文章信息
   */
  article: IArticle
  /**
   * 推荐数据
   */
  recommend: IRecommend[]
  /**
   * 作者信息
   */
  author: IAuthor
  /**
   * 相关车系
   */
  cars: ICarSystem[]
  /**
   * 是否是视频
   */
  isVideo: boolean
  /**
   * 相关视频数据
   */
  videos: IFeedData[]
  /**
   * 每日热点数据
   */
  hotSpot: IFeedData[]
}

function Article({
  gid,
  cars,
  author,
  isVideo,
  article,
  videos,
  hotSpot,
}: IProps) {
  const flagDom = useRef<HTMLDivElement | null>(null)
  const initialTop = useRef(0)
  const processNow = useRef(0)
  const [author2, setAuthor2] = useState<IAuthor>(author)
  const getAuthor = () =>
    fetch(
      getUrl(
        `/motor/info/api/2/article/information/v18/?group_id=${gid}&version_code=444&device_platform=web&aid=36&app_name=automobile`,
        'motor.information.api'
      )
    )
      .then(json => {
        setAuthor2({
          ...author,
          // 异步更新关注态，注：subcribed是后端的拼写错误
          subscribed: get(json, 'data.media_info.subcribed', 0),
          fans_count: get(
            json,
            'data.motor_profile_info.to_user_fans_count',
            ''
          ),
        })
      })
      .catch(err => {
        // no-op
      })

  const commentRef = (node: HTMLDivElement) => {
    if (node === null) {
      return
    }
    initialTop.current =
      node && getBoundingClientRect(node).top + window.pageYOffset
    flagDom.current = node
  }

  useEffect(() => {
    getAuthor()
  }, [])
  useEffect(() => {
    function preventDefault(e: any) {
      if (e.keyCode == 32 && e.target.nodeName === 'VIDEO') {
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', preventDefault)
    return () => {
      window.removeEventListener('keydown', preventDefault)
    }
  }, [])

  const contentTypeObj = ARTICLE_CONTENT_TYPE[article.article_type] || {}

  useEffect(() => {
    sendTeaCommEvent('go_detail', {
      group_id: gid,
      content_type: contentTypeObj.value,
    })
    return () => {
      // pass
    }
  }, [contentTypeObj.value, gid])

  const pageTitle = get(article, 'title') || ''
  const description = (get(article, 'content', '') || '')
    .replace(/<[^>]+>/g, '')
    .slice(0, 100)

  useHandleBeforeLeave(processNow, gid)

  useProcessSpy(flagDom, processNow, initialTop, () =>
    sendTeaCommEvent('article_read_over', {
      group_id: gid,
    })
  )

  return (
    <div className="article clearfix">
      <Head
        title={`${pageTitle ? `${pageTitle} - 懂车帝` : '懂车帝'}`}
        keywords={
          isVideo
            ? `${article.source},${pageTitle},${DEFAULT_ARTICLE_KEYWORDS}`
            : '最新汽车资讯,最新汽车,汽车资讯,汽车文章,文章,图文,试驾,试乘,帝造计划,懂车帝出品'
        }
        description={
          isVideo
            ? `懂车帝视频，由${article.source}发布，视频标题为${pageTitle}`
            : description || DEFAULT_ARTICLE_DESCRIPTION
        }
      />
      <section className="content fl">
        <Breadcrumb
          path={[{ name: '首页', href: '/home', as: '/' }, { name: '正文' }]}
        />
        <Content article={article} isVideo={isVideo} group_id={gid} />
        <div className="comment" ref={commentRef}>
          {isVideo || article.content ? (
            <Comment gid={gid} author_id={author.user_id} />
          ) : null}
        </div>
        <Recommend gid={gid} contentType={contentTypeObj.value} />
      </section>
      <aside className="leaderboard fl">
        {author2 && author2.name ? <Author gid={gid} author={author2} /> : null}
        {cars.length ? <CarSystem gid={gid} cars={cars} /> : null}
        {hotSpot.length > 0 && <HotSpot hotSpot={hotSpot} />}
        <RaletedVideo videos={videos} />
        <HotLeaderBoard />
        <QRCode />
        <Statement />
      </aside>
      <style jsx>
        {`
          .content {
            padding-right: 50px;
          }
          .article {
            width: 1190px;
            margin: 10px auto 0;
          }
          .content {
            width: 790px;
          }
          .leaderboard {
            width: 390px;
            margin-left: 10px;
            padding-top: 60px;
          }
        `}
      </style>
    </div>
  )
}

function handlePath(asPath: string) {
  const search = typeof asPath !== 'undefined' && asPath && asPath.split('?')[1]
  const queries = {} as any
  if (search) {
    search.split('&').forEach(function(c: string) {
      if (c === '') {
        return
      }
      const splitArr = c.split('=')
      const [key, value] = splitArr
      queries[key] = value
    })
  }
  return queries
}

Article.getInitialProps = async (ctx: any) => {
  const { query, asPath, req } = ctx
  /* 获取文章信息 */
  const article = async () => {
    const gidNum = Number(query.gid)
    if (Number.isNaN(gidNum) || gidNum <= 0) {
      return {
        article: {},
      }
    }
    const json = await fetch(
      getUrl(
        `/motor/m/article/detail?group_id=${query.gid}&source=pc`,
        'motor.mobile.web'
      ),
      { req }
    ).catch(err => {
      // no-op
    })
    const article = json?.data || {}
    return {
      article,
    }
  }

  const articleData = await article()

  // UGC文章重定向到 /ugc/article/:group_id 、/ugc/koubei/:group_id
  const isPGCArticle = get(articleData, 'article.is_pgc_article', true)
  if (!isPGCArticle) {
    const isKoubei = get(articleData, 'article.is_koubei') || 0
    const redirectPath = isKoubei ? '/ugc/koubei/' : '/ugc/article/'
    redirectTo(
      `${redirectPath}${query.gid}?zt=${handlePath(asPath).zt || 'redirect'}`,
      { res: ctx.res, status: 301 }
    )
  }

  /* 获取相关车系数据 */
  const cars = async () => {
    const json = await fetch(
      getUrl(
        `/motor/info/api/2/article/information/v18/?group_id=${query.gid}&version_code=444&device_platform=web&aid=36&app_name=automobile`,
        'motor.information.api'
      ),
      { req }
    ).catch(e => {
      // no-op
    })
    return {
      author: Object.assign({}, get(json, 'data.media_info', {}), {
        fans_count: get(json, 'data.motor_profile_info.to_user_fans_count', ''),
      }),
      cars: get(json, 'data.motor_material_car_info[0].info.list', []),
    }
  }

  /* 获取相关视频数据 */
  const videos = async () => {
    let res: IFeedData[]
    try {
      res = await fetchFeed(
        {
          tt_from: 'enter_auto',
          category: 'motor_car_video',
          count: 4,
          max_behot_time: 0,
        },
        req
      ).then(data => {
        return data.data
      })
    } catch {
      res = []
    }
    return res
  }

  /* 获取每日热点数据 */
  const hotSpot = async () => {
    let res
    try {
      res = await fetch(
        getUrl('/motor/m_api/pc_recommend_articles', 'motor.m_station.api'),
        {
          req,
        }
      ).then(json => {
        const result = json?.data?.filter(
          (e: { gid_str: string }) => e?.gid_str !== String(query.gid)
        )
        return result || []
      })
    } catch (e) {
      console.error('fetch hotspot error', e)
      res = []
    }
    return res
  }

  const data = await Promise.all([
    Promise.resolve(articleData),
    cars(),
    videos(),
    hotSpot(),
  ])
  return {
    gid: query.gid,
    article: data[0].article,
    cars: data[1].cars,
    author: { user_id: data[1].author?.to_user_id, ...data[1].author },
    isVideo: !!data[0].article?.video_info,
    videos: data[2],
    hotSpot: data[3],
  }
}

export default Article
